/**
 * THE PORTAL, WIRED UP.
 *
 * Deliberately built to avoid the three routing defects that cost a week on the
 * consumer-facing site, all of which shared one shape: a decision that depended
 * on what a component happened to remember.
 *
 *   1. A failed lookup returned null, which read as "you have no access" and
 *      showed an approved person the wrong screen. Here a failure is a distinct
 *      value (LOOKUP_FAILED) and produces a stated error, never an empty menu.
 *
 *   2. onAuthStateChange fires INITIAL_SESSION on subscribe, racing
 *      getSession() on every cold start. Both are handled by one resolver that
 *      is safe to call twice.
 *
 *   3. Session state lived in a component that unmounted behind a loading
 *      screen, so the memory was destroyed exactly when it was needed. Here
 *      everything lives in App, which never unmounts, and the screens are pure
 *      renderers of what they are handed.
 *
 * THE ONE SECURITY STATEMENT WORTH MAKING ABOUT THIS FILE
 *
 * Nothing here is a security boundary. `cards` is a list of what the DATABASE
 * said this person may see; RLS, admin_has_permission and the SECURITY DEFINER
 * functions are what actually refuse. If somebody edits this bundle in their
 * browser to render all fourteen cards, they get fourteen cards that return
 * nothing, because fn_my_card_permissions asks the database again.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from './lib/supabaseClient';
import {
  O, SANS, Card, Field, QuietButton, PrimaryCTA, FullPageLoader, textInputStyle,
  CodeBoxes, Spinner,
} from './ui';
import {
  fetchMyCards, fetchMyStaffAccount, fetchMyStaffInvite, claimMyStaffSeat,
} from './lib/portal';
import { assuranceLevel, clearTheSecondFactor, refreshMyMfaState } from './lib/mfa';
import useIdleSignOut, { IdleWarning } from './useIdleSignOut';
import AuthConfirm from './AuthConfirm';
import SignIn from './SignIn';
import Portal from './Portal';
import FieldOnboardingScreen from './modules/FieldOnboardingScreen';
import ResourcesScreen from './modules/ResourcesScreen';
import CardScreen from './CardScreen';
import PortalShell from './PortalShell';
import MasterDashboard from './MasterDashboard';
import ResetPassword from './ResetPassword';

// A failed lookup is NOT an empty result. Conflating them is what showed an
// approved provider the role picker on the consumer site; here it would show a
// Compliance officer an empty portal and let them conclude their access had
// been revoked.
const LOOKUP_FAILED = Symbol('lookup failed');

const Centred = ({ children }) => (
  <main style={{
    minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20, fontFamily: SANS, color: O.ink, background: O.canvas,
  }}>
    <div style={{ width: '100%', maxWidth: 420 }}>{children}</div>
  </main>
);

// The founder's ruling, and the only loading state in the portal: the
// green-background mark from the marketing site, cropped circular, with a gold
// ring turning around it. No text, because a cold start has nothing true to say
// about what it is waiting for.
const Loading = () => <FullPageLoader />;

const Unreachable = ({ detail, onRetry }) => (
  <Centred>
    <Card>
      <p style={{ fontSize: 14, color: O.ink }}>{detail}</p>
      <p style={{ marginTop: 7, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
        Nothing has changed. This is a failure to read, not a change in what you may do.
      </p>
      <div style={{ marginTop: 16 }}><PrimaryCTA onClick={onRetry}>Try again</PrimaryCTA></div>
    </Card>
  </Centred>
);

/**
 * Somebody with a valid session who is not staff. Canon 1.2, in one screen.
 *
 * AND THE ONE CASE WHERE THAT IS NOT THE END OF IT.
 *
 *   "I added the user in Supabase with the gali email and a unique password,
 *    but when I use those credentials on the admin site, it says it's not a
 *    staff account."
 *
 * Correct, and a dead end. Becoming staff is three steps — somebody invites the
 * address, the person signs in with it, the person claims the seat — and the
 * third had a database function (fn_claim_my_staff_seat), a read side
 * (fn_my_staff_invite), and NO DOOR ANYWHERE. An invited colleague signed in,
 * was told they were not staff, and was offered a Sign out button.
 *
 * So this screen asks the question before it answers. If a seat is waiting for
 * this confirmed address, it says whose and offers to take it. Canon 1.2 is
 * untouched: the invitation is what grants access, not the login, and the login
 * only ever attaches to a seat somebody with manage_staff_accounts created.
 */
const NotStaff = ({ email, onSignOut, onClaimed }) => {
  const [invite, setInvite] = useState(undefined);   // undefined = still asking
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [idNumber, setIdNumber] = useState('');

  useEffect(() => {
    let alive = true;
    fetchMyStaffInvite().then(({ data, error }) => {
      if (!alive) return;
      // A lookup that failed is not "no invitation". Saying "not staff" on a
      // dropped request is the same mistake this portal already fixed once.
      if (error) { setFailure(error); setInvite(null); return; }
      setInvite(data);
    });
    return () => { alive = false; };
  }, []);

  // The seat may already carry an identity — somebody withdrew an invitation
  // and re-issued it for a person Orchid already had on file — and asking
  // again for something already known is how a form teaches people to guess.
  const needsId = !invite?.id_already_known;

  const claim = async () => {
    // ASKED HERE BECAUSE THIS IS WHERE THE PERSON IS.
    //
    // The OID names somebody in admin_activity_log for the life of the company.
    // Tied to an admin_users row it dies with the row; tied to the hash of an
    // ID number it is theirs, and somebody who leaves and comes back in 2031 is
    // recognised rather than re-issued. The database accepts a claim without
    // one — it must, or a database ahead of this deploy would lock people out
    // of a door that exists — so the requirement is the screen's.
    if (needsId && !idNumber.trim()) {
      setFailure('Your ID number, so your OID stays yours if you ever leave and come back.');
      return;
    }
    setBusy(true);
    setFailure(null);
    const { data, error } = await claimMyStaffSeat(idNumber);
    setBusy(false);
    if (error) { setFailure(error); return; }
    onClaimed(data);
  };

  if (invite === undefined) return <Loading />;

  if (invite) {
    return (
      <Centred>
        <Card>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: O.ink }}>
            {invite.full_name}, your seat is waiting.
          </h1>
          <p style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
            Somebody at Orchid added <strong>{invite.email}</strong> to the staff
            {invite.job_title ? ` as ${invite.job_title}` : ''}. Taking it attaches this
            login to that seat — nothing else on this account changes, and no permission
            is granted here that was not granted there.
          </p>
          {needsId && (
            <div style={{ marginTop: 14 }}>
              <Field
                label="Your ID number"
                htmlFor="claim-id-number"
                hint="Held as a one-way hash, never as a number. It is what makes your OID yours: if you ever leave Orchid and come back, you are recognised and given the same one rather than a new one."
              >
                <input
                  id="claim-id-number"
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={idNumber}
                  onChange={(e) => setIdNumber(e.target.value)}
                  style={textInputStyle(false)}
                />
              </Field>
            </div>
          )}
          {failure && (
            <p style={{ marginTop: 12, fontSize: 12.5, lineHeight: 1.6, color: O.critical }}>
              {failure}
            </p>
          )}
          <div style={{ marginTop: 16 }}>
            <PrimaryCTA onClick={claim} busy={busy} disabled={busy}>
              {busy ? 'Opening your access…' : 'Take my seat'}
            </PrimaryCTA>
          </div>
          <div style={{ marginTop: 10 }}>
            <QuietButton onClick={onSignOut}>Sign out</QuietButton>
          </div>
        </Card>
      </Centred>
    );
  }

  return (
    <Centred>
      <Card>
        <h1 style={{ fontSize: 16, fontWeight: 700, color: O.ink }}>
          This account is not a staff account.
        </h1>
        <p style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
          Staff identity is a separate system from platform identity. An Orchid account
          for using the platform grants nothing here, even for the same person.
        </p>
        {/* WHAT ACTUALLY HAS TO HAPPEN, because "you are not staff" is a fact
            and not an instruction. Creating a login in Supabase is not the same
            act as being given a seat, and the address is named because signing
            in on a different one from the invited one is the likeliest reason
            somebody who WAS invited lands here. */}
        <p style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
          You are signed in as <strong>{email || 'this address'}</strong>, and no staff
          seat has been created for it. Somebody who already holds Orchid staff access has
          to add that exact address under People &amp; Access first. A login on its own
          grants nothing.
        </p>
        {failure && (
          <p style={{ marginTop: 12, fontSize: 12.5, lineHeight: 1.6, color: O.critical }}>
            {failure}
          </p>
        )}
        <div style={{ marginTop: 16 }}><QuietButton onClick={onSignOut}>Sign out</QuietButton></div>
      </Card>
    </Centred>
  );
};

/**
 * HALF A SIGN-IN.
 *
 * Supabase stamps a session aal1 when a password was the only thing proved, and
 * aal2 once a factor has been challenged and verified IN THAT SESSION. Somebody
 * enrolled who is sitting at aal1 has typed a password and stopped.
 *
 * Enrolling and then never being asked is a company with a security control it
 * does not have. This is where it is asked for. The database asks too --
 * fn_log_my_admin_action refuses an aal1 session for anybody carrying a
 * verified factor -- so bypassing this screen buys nothing.
 */
const SecondFactor = ({ email, onCleared, onSignOut }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  /**
   * TWO MINUTES, THEN BACK TO THE START.
   *
   *   "when any staff is logging in and the authentication page is waiting for
   *    the code, it must count down from 120 seconds, then revert to the login
   *    screen for a fresh input of login credentials."
   *
   * A half-finished sign-in left open is a password already accepted sitting on
   * an unattended screen, waiting for anybody who picks up the phone next to it.
   * The window closes on its own.
   *
   * IT SIGNS OUT RATHER THAN JUST NAVIGATING. Sending somebody back to the login
   * form while the aal1 session survives would be a screen change dressed up as
   * a security control — the session would still be there, and so would whatever
   * an aal1 session can reach. onSignOut is the same path the button below uses.
   *
   * The interval is cleared on unmount, so somebody who types their code with
   * three seconds left is not signed out a moment after succeeding.
   */
  const [secondsLeft, setSecondsLeft] = useState(120);

  useEffect(() => {
    const tick = setInterval(() => {
      setSecondsLeft((n) => (n <= 1 ? 0 : n - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (secondsLeft === 0) onSignOut();
  }, [secondsLeft, onSignOut]);

  /**
   * THE SIXTH DIGIT IS THE ENTER KEY.
   *
   *   "after typing the 6th digit, that must basically mean 'enter' and let me
   *    into the Portal is the digits are correct. No more 'Continue'."
   *    -- the founder, 14 September 2026
   *
   * useCallback, and not for tidiness: CodeBoxes fires this from an effect
   * keyed on the value, so a new function identity on every render would make
   * that effect re-run and the code would be submitted twice.
   */
  const submit = useCallback(async (typed) => {
    const entered = typed ?? code;
    setBusy(true);
    setFailure(null);
    const { error } = await clearTheSecondFactor(entered);
    if (error) {
      setBusy(false);
      setFailure(error);
      // CLEARED, AND THAT IS NOT TIDINESS EITHER. With no button left, a
      // refused code sitting in the boxes is six digits the person cannot
      // resubmit -- the row is already full, so nothing fires. Emptying it is
      // what gives them their next attempt, and it is also what a person does
      // by hand when a code is refused.
      setCode('');
      return;
    }
    // The database keeps its own record of who is enrolled, and it keeps it by
    // LOOKING rather than by being told. Refreshing here means a factor removed
    // from the Supabase dashboard stops counting on the next sign-in.
    await refreshMyMfaState();
    setBusy(false);
    onCleared();
  }, [code, onCleared]);

  return (
    <Centred>
      <Card>
        <h1 style={{ fontSize: 16, fontWeight: 700, color: O.ink }}>
          Enter the code from your authenticator.
        </h1>
        <p style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
          {email ? <>Signed in as <strong>{email}</strong>. </> : null}
          Open the app you set Orchid up in and type the six digits showing now.
        </p>
        <div style={{ marginTop: 14 }}>
          <Field label="Six-digit code" htmlFor="second-factor-code">
            <CodeBoxes
              value={code}
              onChange={setCode}
              onComplete={submit}
              disabled={busy}
              invalid={Boolean(failure)}
              autoFocus
            />
          </Field>
        </div>
        {failure && (
          <p style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.6, color: O.critical }}>
            {failure}
          </p>
        )}

        {/* NO BUTTON, AND SOMETHING IN ITS PLACE. A row of boxes that submits
            itself needs to say that it is doing so, or the moment between the
            sixth digit and the portal opening reads as a screen that has
            stopped responding. This line is that moment, said out loud. */}
        <p role="status" style={{
          marginTop: 12, fontSize: 12, lineHeight: 1.6,
          color: busy ? O.ink : O.inkFaint,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {busy && <Spinner size={13} />}
          {busy ? 'Checking your code…' : 'The portal opens as soon as the sixth digit is in.'}
        </p>

        {/* SAID, NOT HIDDEN. Somebody watching a number fall knows the screen
            is going to close and can decide to hurry or to start again; a
            screen that closes without warning reads as a crash. */}
        <p style={{
          marginTop: 12, fontSize: 11.5, lineHeight: 1.6,
          color: secondsLeft <= 20 ? O.critical : O.inkFaint,
        }}>
          {secondsLeft > 0
            ? `This page closes in ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')} and you will sign in again from the start.`
            : 'Time is up. Signing you out…'}
        </p>

        {/* REWRITTEN BY SCRIPT 118. This used to say no button here could help,
            which was true of Supabase's client library and true of this portal
            until Human Capital was given a way to do it. Sending somebody to
            the founder for something a colleague can now do in ten seconds is
            the kind of copy that outlives the thing it described. */}
        <p style={{ marginTop: 14, fontSize: 11.5, lineHeight: 1.6, color: O.inkFaint }}>
          Lost that phone? Ask Human Capital to remove your authenticator from
          the staff directory. You will be able to set up a new one the next time
          you sign in, and you hold no MFA permission until you do.
        </p>
        <div style={{ marginTop: 10 }}><QuietButton onClick={onSignOut}>Sign out</QuietButton></div>
      </Card>
    </Centred>
  );
};

export default function App() {
  const [session, setSession] = useState(undefined); // undefined = not resolved
  const [staff, setStaff] = useState(undefined);
  const [cards, setCards] = useState(undefined);
  const [error, setError] = useState(null);
  // undefined = not asked yet; true = a factor on this account is still to be
  // cleared in this session.
  const [secondFactor, setSecondFactor] = useState(undefined);

  // Guards a second concurrent load rather than a second render: the
  // INITIAL_SESSION / getSession race means load() genuinely can be called
  // twice within a tick, and two flights would set state out of order.
  const loadingRef = useRef(false);

  const load = useCallback(async (activeSession) => {
    if (!activeSession) {
      setStaff(null);
      setCards(null);
      setSecondFactor(undefined);
      setError(null);
      return;
    }
    if (loadingRef.current) return;
    loadingRef.current = true;

    // THE BUG THE FOUNDER SAW, AND IT IS THIS LINE'S ABSENCE.
    //
    //   "After clicking 'sign in,' the loading screen must not say 'this
    //    account is not a staff account' as a loading state ... that screen
    //    must show only if truly the account being used to log in REALLY
    //    ISN'T a staff account."
    //
    // App distinguishes three values: undefined is "not asked yet", null is
    // "asked, and there is no staff row", LOOKUP_FAILED is "could not ask".
    // The render below turns null into the not-staff screen, correctly.
    //
    // But signing out sets staff to null, and so does the initial no-session
    // pass. So when a session arrived, load() started fetching while staff was
    // still null from before -- and React rendered the not-staff screen for the
    // whole flight. The account was fine. The portal was telling somebody they
    // were not staff while it was still finding out.
    //
    // Resetting to undefined here is the fix: "asking" is its own state again,
    // and the not-staff screen can only appear after an answer.
    setStaff(undefined);
    setCards(undefined);
    setError(null);

    try {
      // The assurance level is asked for alongside the other two rather than
      // after them: it decides which screen this person sees, and asking for it
      // in a second round trip would mean the portal renders once and then
      // replaces itself.
      const [staffRes, cardsRes, aalRes] = await Promise.all([
        fetchMyStaffAccount(), fetchMyCards(), assuranceLevel(),
      ]);

      // A level that could not be read is NOT "a code is owed". Demanding one
      // because a lookup failed would lock out whoever has the worst
      // connection, and the database refuses an aal1 session on its own anyway.
      setSecondFactor(aalRes.error ? false : Boolean(aalRes.data?.mustVerify));

      if (staffRes.error || cardsRes.error) {
        setError(staffRes.error || cardsRes.error);
        setStaff(LOOKUP_FAILED);
        setCards(LOOKUP_FAILED);
        return;
      }
      setError(null);
      setStaff(staffRes.data);
      setCards(cardsRes.data);
    } finally {
      // finally, not a trailing statement. A promise that rejects here would
      // otherwise leave the flag set and every later attempt would return
      // immediately, for ever, with no error on screen.
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      setSession(null);
      return undefined;
    }
    let alive = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data?.session || null);
      load(data?.session || null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (!alive) return;
      setSession(next || null);
      // A token refresh changes nothing about who this person is, and
      // re-fetching on it is what made a backgrounded tab come back to a
      // spinner on the consumer site.
      if (event === 'TOKEN_REFRESHED') return;
      load(next || null);
    });

    return () => {
      alive = false;
      sub?.subscription?.unsubscribe();
    };
  }, [load]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setStaff(null);
    setCards(null);
  };

  const retry = () => {
    setError(null);
    setStaff(undefined);
    setCards(undefined);
    load(session);
  };

  /**
   * TEN MINUTES.
   *
   *   "every Orchid Portal login should have a timed non-activity auto-logout,
   *    so that if a staff member has not been using the system for 10 minutes,
   *    they are automatically logged out."
   *
   * Armed only while somebody is actually inside -- an idle timer running on
   * the sign-in screen would sign out a session that does not exist, and one
   * running during the staff lookup would race it.
   *
   * The last minute is spent warning rather than counting silently. This is not
   * a softening of the rule: the ten minutes are unchanged, and the warning is
   * what stops a Compliance officer losing a half-typed reason capture to a
   * clock they could not see.
   */
  const { secondsLeft: idleWarning, stay: staySignedIn } = useIdleSignOut({
    enabled: Boolean(session) && Boolean(staff) && staff !== LOOKUP_FAILED,
    minutes: 10,
    warnSeconds: 60,
    onIdle: signOut,
  });

  /**
   * RECOVERY COMES BEFORE EVERYTHING, INCLUDING THE STAFF LOOKUP.
   *
   * A recovery link hands over a REAL session before this component renders,
   * so without this branch a person clicking "I have forgotten my password"
   * would be carried straight past the form and into the portal -- signed in
   * with the password they came here because they had forgotten. That is the
   * second half of "the link doesn't work".
   *
   * Read from the URL rather than from the router, because the router is
   * mounted further down, after gates this flow has to bypass.
   */
  const onResetRoute = typeof window !== 'undefined'
    && window.location.pathname.replace(/\/+$/, '') === '/reset';
  if (onResetRoute) {
    return <ResetPassword onDone={() => { window.location.replace('/'); }} />;
  }

  if (session === undefined) return <Loading />;
  if (!session) return <SignIn />;
  if (staff === undefined || cards === undefined) return <Loading />;
  if (staff === LOOKUP_FAILED || cards === LOOKUP_FAILED) {
    return <Unreachable detail={error || 'Orchid could not reach the network just now.'} onRetry={retry} />;
  }
  // HALF A SIGN-IN, BEFORE ANYTHING ELSE IS SHOWN. Only for somebody who
  // actually carries a factor -- nextLevel is aal2 only when there is one to
  // clear -- so nobody who has not enrolled is ever asked for a code.
  if (secondFactor) {
    return (
      <SecondFactor
        email={session?.user?.email}
        onSignOut={signOut}
        onCleared={() => { setSecondFactor(false); load(session); }}
      />
    );
  }

  // A session with no staff row: real, and not an error. Canon 1.2.
  if (!staff) {
    return (
      <NotStaff
        email={session?.user?.email}
        onSignOut={signOut}
        onClaimed={() => load(session)}
      />
    );
  }

  // Suspended or locked out. The portal says so rather than showing an empty
  // menu, which would read as "your permissions were removed".
  if (staff.status !== 'active') {
    return (
      <Centred>
        <Card>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: O.ink }}>
            This staff account is {staff.status}.
          </h1>
          <p style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted }}>
            Nothing has been removed from it. People &amp; Access can reinstate it.
          </p>
          <div style={{ marginTop: 16 }}><QuietButton onClick={signOut}>Sign out</QuietButton></div>
        </Card>
      </Centred>
    );
  }

  const hasMaster = (cards || []).some((c) => c.is_master);

  // A CONFIRMATION LINK LANDS HERE, BEFORE THE ROUTER.
  //
  // By the time this renders Supabase has already exchanged the token, so
  // somebody arriving on the link looks like an ordinary signed-in operator
  // and every gate below would carry them into the Portal without ever saying
  // what the link did. Before the router for the same reason the platform
  // does it: the catch-all Route would swallow the path first.
  const onConfirmRoute = typeof window !== 'undefined'
    && window.location.pathname.replace(/\/+$/, '') === '/auth/confirm';
  if (onConfirmRoute) return <AuthConfirm />;

  return (
    <BrowserRouter>
      <IdleWarning secondsLeft={idleWarning} onStay={staySignedIn} />
      <Routes>
        <Route
          path="/"
          element={(
            <PortalShell cards={cards}>
              {({ attention }) => (
                <Portal
                  cards={cards}
                  staff={staff}
                  onSignOut={signOut}
                  attention={attention}
                />
              )}
            </PortalShell>
          )}
        />
        {/* FIELD ONBOARDING HAS ITS OWN ADDRESS NOW -- "that has its own screen
            now". It was a panel at the top of Overview reached by an anchor;
            it is a destination. */}
        <Route
          path="/field-onboarding"
          element={(
            <PortalShell cards={cards}>
              {({ permissions }) => (
                <FieldOnboardingScreen
                  staff={staff}
                  onSignOut={signOut}
                  permissions={permissions}
                />
              )}
            </PortalShell>
          )}
        />
        {/* RESOURCES IS A FIXED DESTINATION AND NOT A CARD, because the table
            behind it is readable by every active member of staff and by no
            permission at all (script 59, and script 330 kept the ruling). A
            card is drawn only for somebody holding a permission inside it, so
            making this one would shut out the new joiner it exists for. */}
        <Route
          path="/resources"
          element={(
            <PortalShell cards={cards}>
              {({ permissions }) => (
                <ResourcesScreen
                  staff={staff}
                  onSignOut={signOut}
                  cards={cards}
                  permissions={permissions}
                />
              )}
            </PortalShell>
          )}
        />
        <Route
          path="/card/:slug"
          element={<PortalShell cards={cards}><CardScreen cards={cards} /></PortalShell>}
        />
        <Route
          path="/master"
          // Not a security check — the database is. This stops somebody who
          // typed the address by hand from staring at a screen built for
          // somebody else and drawing conclusions from its empty panels.
          element={hasMaster
            ? (
              <PortalShell cards={cards}>
                <MasterDashboard cards={cards} staff={staff} />
              </PortalShell>
            )
            : <Navigate to="/" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
